import React, { useContext, useMemo, useState } from 'react'

import { flatMap } from 'lodash'
import { useFeedbackModalStore } from 'store/feedbackModalStore'
import { useFiltersModalStore } from 'store/filtersModalStore'
import { shallow } from 'zustand/shallow'

import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  InfoBadge,
  Select,
  SelectGroupedOptions,
  styled,
  Text
} from '@driftt/dds'
import { faFilter } from '@driftt/dds.icons/light/faFilter'
import { faGear } from '@driftt/dds.icons/light/faGear'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import { MAX_TABLE_RECORDS } from 'components/AccountEngagementTable'
import { FilterButtonTooltip } from 'components/FilterButtonTooltip'
import PreferencesModal from 'components/PreferencesModal'
import SaveFilterModal from 'components/SaveFilterModal'
import ActiveFilters from 'components/SortAndFilter/ActiveFilters'
import { ICPSettingsContext } from 'context/icpSettingsContext'
import { useAttributeFilters } from 'hooks/useAttributeFilters'
import useFilterConfiguration from 'hooks/useFilterConfiguration'
import { useAppSelector } from 'hooks/useRedux'
import useSavedFilterEditModal from 'hooks/useSavedFilterEditModal'
import CCRMFiltersCard from 'sections/CCRM/FiltersCard'
import { FilterPage } from 'utils/filters'
import LocalPersistence from 'utils/localPersistence'
import { segmentTrack, TrackType } from 'utils/tracking'

import SearchBar from './SearchBar'

const HAS_SEEN_FEEDBACK_MODAL = 'HAS_SEEN_FEEDBACK_MODAL'

export interface DashboardFiltersProps {
  activeSavedFilter: DeserializedSavedFilter | undefined
  updateActiveFilterId: (id?: number) => void
  sharedFilters: DeserializedSavedFilter[]
  privateFilters: DeserializedSavedFilter[]
  accountDataSize: number | undefined
  resultSize: number | undefined
  savedFilter?: DeserializedSavedFilter
  filterSelectOpen?: boolean
}

const StyledSelect = styled(Select, {
  top: '-0.15rem'
})

const generateFilterOptions = (
  filters: Partial<DeserializedSavedFilter>[],
  label: string,
  accountFilterId?: number
): SelectGroupedOptions => {
  const options = filters.map(({ id = '', name = '' }) => {
    if (accountFilterId === id && accountFilterId !== 0) {
      return {
        value: `${id}`,
        label: name,
        suffix: <InfoBadge>Default</InfoBadge>
      }
    }
    return {
      value: `${id}`,
      label: name
    }
  })

  return { label, options }
}

const DashboardFilters = ({
  updateActiveFilterId,
  activeSavedFilter,
  sharedFilters,
  privateFilters,
  accountDataSize,
  resultSize,
  filterSelectOpen = false
}: DashboardFiltersProps) => {
  const { showPreferencesModal, setShowPreferencesModal } = useContext(ICPSettingsContext)
  const { showModal, closeModal, openModal, toggleModal } = useFiltersModalStore(
    state => ({
      showModal: state.showModal,
      closeModal: state.closeModal,
      openModal: state.openModal,
      toggleModal: state.toggleModal
    }),
    shallow
  )
  const { toggleModal: openFeedBackModal } = useFeedbackModalStore(
    state => ({
      toggleModal: state.toggleModal
    }),
    shallow
  )
  const accountFilter = useMemo(() => ({ name: 'All accounts', id: 0 }), [])
  const [showSavedFilterModal, setShowSavedFilterModal] = useState(false)
  const { defaultFilterId, filterGroupsByPage } = useAppSelector(({ filters }) => filters)
  const { name, id } = activeSavedFilter || accountFilter
  const selectOptions = useMemo(
    () => [
      generateFilterOptions([accountFilter], 'Accounts'),
      generateFilterOptions(privateFilters, 'Your saved filters', defaultFilterId),
      generateFilterOptions(sharedFilters, 'Shared saved filters', defaultFilterId)
    ],
    [privateFilters, sharedFilters, accountFilter, defaultFilterId]
  )
  const attributeFilters = useAttributeFilters()
  const filterConfig = useFilterConfiguration(attributeFilters)

  const {
    editableFilterGroups,
    editableSavedFilter,
    onCloseModal,
    onOpenEditModal
  } = useSavedFilterEditModal()
  const handleSaveFilterModalClose = () => {
    setShowSavedFilterModal(false)
    onCloseModal()
  }

  const handleFilterChange = (value?: string) => {
    if (!value) {
      segmentTrack(TrackType.SAVED_FILTER_SELECTED_DASHBOARD, { filterId: value, isDefault: true })
      return updateActiveFilterId(undefined)
    }
    segmentTrack(TrackType.SAVED_FILTER_SELECTED_DASHBOARD, { filterId: value })
    updateActiveFilterId(+value)
  }

  const handleFeedbackModalOpen = () => {
    if (LocalPersistence.get(HAS_SEEN_FEEDBACK_MODAL)) {
      return
    }

    openFeedBackModal(true)
    LocalPersistence.set(HAS_SEEN_FEEDBACK_MODAL, true)
  }

  const filterGroups: any[] = flatMap(filterGroupsByPage.ACTIVITIES)
  const selectProps: Record<string, any> = {}

  if (filterSelectOpen === true) {
    selectProps.menuIsOpen = filterSelectOpen
  }
  return (
    <Flex direction="column" gap={2} fullWidth>
      <Flex alignItems="flex-start" justifyContent="space-between" css={{ padding: 0 }} fullWidth>
        <SearchBar />
        <Flex gap={1} alignItems="center" justifyContent="center">
          <StyledSelect
            {...selectProps}
            label=""
            isClearable={false}
            options={selectOptions}
            value={{ label: name, value: `${id}` }}
            onChange={({ value }: any) => {
              if (value === '0') {
                return handleFilterChange()
              }
              handleFilterChange(value)
              segmentTrack(TrackType.SAVED_FILTER_SELECTED_DASHBOARD, { filterId: value })
            }}
          />
          <Box
            css={{
              overflowY: 'visible',
              position: 'relative'
            }}>
            <Button
              variant="secondary"
              onClick={() => {
                openModal()
                segmentTrack(TrackType.FILTER_TOGGLED, { page: 'Dashboard' })
              }}>
              <Icon icon={faFilter} /> {filterGroups.length ? `${filterGroups.length} ` : ''}
              Filters
            </Button>

            {showModal && (
              <Box
                css={{
                  top: 40,
                  right: 0,
                  flex: 1,
                  width: 720,
                  position: 'absolute',
                  zIndex: '$modal'
                }}>
                <CCRMFiltersCard
                  useAccountFilters
                  onClose={() => {
                    closeModal()
                    handleFeedbackModalOpen()
                  }}
                  onOpenEditModal={(_, groups) => {
                    onOpenEditModal(
                      undefined,
                      editableFilterGroups.length ? editableFilterGroups : groups
                    )
                    closeModal()
                    setShowSavedFilterModal(true)
                  }}
                  setActiveFilterId={(id: any) => handleFilterChange(`${id}`)}
                />
              </Box>
            )}
            {showSavedFilterModal && (
              <SaveFilterModal
                editableGroups={
                  editableFilterGroups.length ? editableFilterGroups : filterGroupsByPage.ACTIVITIES
                }
                savedFilter={editableSavedFilter}
                onClose={handleSaveFilterModalClose}
              />
            )}
            <FilterButtonTooltip setShowFiltersMenu={toggleModal} />
          </Box>
          <IconButton
            onClick={() => {
              setShowPreferencesModal(true)
              segmentTrack(TrackType.FILTERS_SETTINGS_MODAL_OPENED)
            }}
            size="default"
            className="notifications-onboarding"
            variant="education"
            css={{
              height: '32px',
              width: '32px',
              '&:hover': {
                backgroundColor: '$gray2',
                borderColor: '$gray2'
              }
            }}
            title="Manage preferences"
            icon={faGear}
          />
          <PreferencesModal 
            open={showPreferencesModal}
            setModalOpen={setShowPreferencesModal}
            sharedFilters={sharedFilters}
            privateFilters={privateFilters}/>
        </Flex>
      </Flex>
      <Flex direction="column">
        <Flex
          alignItems="center"
          css={{
            minHeight: 'fit-content'
          }}>
          <Text
            variant="metadata"
            css={{
              justifySelf: 'flex-start'
            }}>
            Showing{' '}
            <Box
              as="span"
              css={{
                fontWeight: '$bold'
              }}>
              {resultSize ?? 0} accounts{' '}
              {accountDataSize && resultSize ? `of ${accountDataSize > MAX_TABLE_RECORDS ? `${MAX_TABLE_RECORDS}+` : accountDataSize}` : ''}
            </Box>
            {filterGroups.length > 0 ? ' | ' : ''}
          </Text>

          {filterGroups.length > 0 && (
            <ActiveFilters filterConfiguration={filterConfig} filterPage={FilterPage.ACTIVITIES} />
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default DashboardFilters
